import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { adminApi } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { clearSession, readSession, UNAUTHORIZED_EVENT, writeSession } from '@/lib/session';

interface AuthContextValue {
  authenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(() => readSession()?.username ?? null);

  // The API client clears the stored session and fires this event on any 401.
  useEffect(() => {
    const onUnauthorized = () => setUsername(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = useCallback(async (user: string, password: string) => {
    const session = await adminApi.login({ username: user, password });
    writeSession(session);
    setUsername(session.username);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    queryClient.clear();
    setUsername(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ authenticated: username !== null, username, login, logout }),
    [username, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
